import { useEffect, useState, useCallback } from "react";
import { adminApi, BACKEND_URL, formatAud } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Search, X, ChevronLeft, ChevronRight, Car, Facebook, Store,
  Package, DollarSign, ExternalLink, CheckCircle2, Info, ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";

const proxyImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http://api.carparts-au.com/")) {
    return `${BACKEND_URL}/api/proxy/image?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const FEW_PARTS_OPTIONS = [
  { label: "All vehicles", value: "" },
  { label: "≤5 parts remaining", value: "5" },
  { label: "≤3 parts remaining", value: "3" },
  { label: "1 part remaining", value: "1" },
];

const SORT_OPTIONS = [
  { label: "Stock # (Low → High)",  value: "stock_asc"  },
  { label: "Stock # (High → Low)",  value: "stock_desc" },
  { label: "Make/Model (A → Z)",    value: "alpha_asc"  },
  { label: "Make/Model (Z → A)",    value: "alpha_desc" },
  { label: "Year (Newest first)",   value: "year_desc"  },
  { label: "Year (Oldest first)",   value: "year_asc"   },
  { label: "Most parts first",      value: "parts_desc" },
  { label: "Fewest parts first",    value: "parts_asc"  },
];

/* ── Shared Facebook listing logic — used by both the row quick-button
   and the drawer, so behaviour (and the "already listed" popup) is
   identical no matter where staff trigger it from. ─────────────────── */
function useFacebookListing(onResult) {
  const [posting, setPosting] = useState(false);

  const listToFacebookPage = async (stockNo) => {
    setPosting(true);
    try {
      const { data: res } = await adminApi.post(`/admin/vehicles/${encodeURIComponent(stockNo)}/list-facebook-page`);
      onResult(stockNo, res);
      toast[res.already_listed ? "info" : "success"](
        res.already_listed ? "Already listed on Facebook" : "Posted to Facebook Page"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post to Facebook");
    } finally {
      setPosting(false);
    }
  };

  const listToMarketplace = async (stockNo) => {
    try {
      const { data: res } = await adminApi.get(`/admin/vehicles/${encodeURIComponent(stockNo)}/marketplace-prefill`);
      window.open(res.prefill_url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build Marketplace listing");
    }
  };

  return { listToFacebookPage, listToMarketplace, posting };
}

export default function VehicleStockView({ onOpenPart }) {
  const [items,     setItems]     = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [pageSize]                = useState(24);
  const [q,         setQ]         = useState("");
  const [maxParts,  setMaxParts]  = useState("");
  const [sort,      setSort]      = useState("stock_asc");
  const [loading,   setLoading]   = useState(true);
  const [pageJump,  setPageJump]  = useState("");
  const [selected,  setSelected]  = useState(null); // stock_no of open drawer
  const [fbResults, setFbResults] = useState({});   // stock_no -> fb listing info, for row badges

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, sort };
      if (q.trim()) params.q = q.trim();
      if (maxParts) params.max_parts = maxParts;
      const { data } = await adminApi.get("/admin/vehicles", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Failed to load vehicle stock");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q, maxParts, sort]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, maxParts, sort]);
  useEffect(() => {
    setPage(1);
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const jumpToPage = () => {
    const n = parseInt(pageJump, 10);
    if (n >= 1 && n <= totalPages) {
      setPage(n);
      setPageJump("");
    } else {
      toast.error(`Enter a page between 1 and ${totalPages}`);
    }
  };

  const { listToFacebookPage, listToMarketplace, posting } = useFacebookListing(
    (stockNo, res) => setFbResults(prev => ({ ...prev, [stockNo]: res }))
  );

  return (
    <div className="space-y-4" data-testid="vehicle-stock-view">

      {/* Search + filter + sort bar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-3 flex-wrap" style={{ border: "1px solid #dde3ef" }}>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#94a3b8" }} />
          <Input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search stock no, make, model, year…"
            className="pl-9 rounded-xl h-10 text-[13px]"
            style={{ background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" }}
            data-testid="vehicle-search-input" />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="h-4 w-4" style={{ color: "#94a3b8" }} />
            </button>
          )}
        </div>
        <select value={maxParts} onChange={e => setMaxParts(e.target.value)}
          className="h-10 px-3 rounded-xl text-[13px] outline-none"
          style={{ background: "#ffffff", border: "1px solid #dde3ef", color: "#0f172a" }}
          data-testid="vehicle-few-parts-filter">
          {FEW_PARTS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="relative">
          <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none" style={{ color: "#94a3b8" }} />
          <select value={sort} onChange={e => setSort(e.target.value)}
            className="h-10 pl-9 pr-3 rounded-xl text-[13px] outline-none appearance-none"
            style={{ background: "#ffffff", border: "1px solid #dde3ef", color: "#0f172a" }}
            data-testid="vehicle-sort-select">
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="text-[12px] font-mono" style={{ color: "#94a3b8" }}>
          {total.toLocaleString()} vehicle{total === 1 ? "" : "s"}
        </div>
      </div>

      {/* Vehicle grid */}
      {loading ? (
        <div className="bg-white rounded-2xl p-16 text-center shadow-sm" style={{ border: "1px solid #dde3ef" }}>
          <div className="w-8 h-8 border-2 rounded-full animate-spin mx-auto mb-3" style={{ borderColor: "#1e5bb8", borderTopColor: "transparent" }} />
          <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Loading…</div>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl p-16 text-center shadow-sm" style={{ border: "1px solid #dde3ef" }}>
          <Car className="h-10 w-10 mx-auto mb-3" style={{ color: "#cbd5e1" }} />
          <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>No vehicles found</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map(v => {
            const fb = fbResults[v.stock_no];
            return (
              <div key={v.stock_no}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all flex"
                style={{ border: "1px solid #dde3ef" }}
                data-testid={`vehicle-row-${v.stock_no}`}>
                <button onClick={() => setSelected(v.stock_no)} className="w-56 h-44 shrink-0 text-left" style={{ background: "#f5f7fb" }}>
                  {v.primary_image ? (
                    <img src={proxyImg(v.primary_image)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Car className="h-10 w-10" style={{ color: "#cbd5e1" }} />
                    </div>
                  )}
                </button>
                <div className="p-4 flex-1 min-w-0 flex flex-col justify-between">
                  <button onClick={() => setSelected(v.stock_no)} className="text-left w-full">
                    <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>
                      Stock #{v.stock_no}
                    </div>
                    <div className="font-heading font-bold text-[17px] truncate" style={{ color: "#0f172a" }}>
                      {[v.vehicle_year, v.vehicle_make, v.vehicle_model].filter(Boolean).join(" ") || "Unknown vehicle"}
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[13px]" style={{ color: "#64748b" }}>
                      <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5" />{v.parts_count} part{v.parts_count === 1 ? "" : "s"}</span>
                      <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />{formatAud(v.total_value)}</span>
                    </div>
                    {v.images.length > 1 && (
                      <div className="text-[11px] mt-1" style={{ color: "#94a3b8" }}>{v.images.length} photos</div>
                    )}
                  </button>

                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => listToFacebookPage(v.stock_no)} disabled={posting}
                      className="h-8 px-3 rounded-lg flex items-center gap-1.5 text-[11px] font-semibold"
                      style={{ background: "#eff6ff", color: "#1e40af", border: "1px solid #93c5fd" }}
                      data-testid={`vehicle-quick-fb-${v.stock_no}`}>
                      <Facebook className="h-3 w-3" /> Page
                    </button>
                    <button onClick={() => listToMarketplace(v.stock_no)}
                      className="h-8 px-3 rounded-lg flex items-center gap-1.5 text-[11px] font-semibold"
                      style={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #86efac" }}
                      data-testid={`vehicle-quick-mp-${v.stock_no}`}>
                      <Store className="h-3 w-3" /> Marketplace
                    </button>
                    {fb && (
                      <span className="flex items-center gap-1 text-[10px] font-mono" style={{ color: fb.already_listed ? "#92400e" : "#15803d" }}>
                        {fb.already_listed ? <Info className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                        {fb.already_listed ? "Already listed" : "Listed"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!loading && total > 0 && (
        <div className="bg-white rounded-2xl p-3 shadow-sm flex items-center justify-between flex-wrap gap-3" style={{ border: "1px solid #dde3ef" }}>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="h-9 w-9 flex items-center justify-center rounded-xl disabled:opacity-30"
              style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
              <ChevronLeft className="h-4 w-4" style={{ color: "#475569" }} />
            </button>
            <span className="text-[12px] font-mono px-2" style={{ color: "#475569" }}>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="h-9 w-9 flex items-center justify-center rounded-xl disabled:opacity-30"
              style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
              <ChevronRight className="h-4 w-4" style={{ color: "#475569" }} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>Jump to</span>
            <input type="number" min={1} max={totalPages} value={pageJump}
              onChange={e => setPageJump(e.target.value)}
              onKeyDown={e => e.key === "Enter" && jumpToPage()}
              className="w-16 h-9 px-2 rounded-xl text-[12px] font-mono outline-none text-center"
              style={{ background: "#ffffff", border: "1px solid #dde3ef", color: "#0f172a" }} />
            <Button size="sm" onClick={jumpToPage} className="rounded-xl text-[12px]" style={{ background: "#1e5bb8", color: "#fff" }}>Go</Button>
          </div>
        </div>
      )}

      {selected && (
        <VehicleDetailDrawer stockNo={selected} onClose={() => setSelected(null)}
          initialFbInfo={fbResults[selected]}
          onFbResult={(res) => setFbResults(prev => ({ ...prev, [selected]: res }))}
          onOpenPart={onOpenPart} />
      )}
    </div>
  );
}

/* ── Detail drawer — details + actions up top, gallery below, parts list ── */
function VehicleDetailDrawer({ stockNo, onClose, initialFbInfo, onFbResult, onOpenPart }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [fbInfo,  setFbInfo]  = useState(initialFbInfo || null);
  const [lightbox, setLightbox] = useState(null);

  const { listToFacebookPage, listToMarketplace, posting } = useFacebookListing((_, res) => {
    setFbInfo(res);
    onFbResult(res);
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await adminApi.get(`/admin/vehicles/${encodeURIComponent(stockNo)}`);
        setData(data);
        if (data.fb_listing && !fbInfo) setFbInfo({ ...data.fb_listing, already_listed: true });
      } catch {
        toast.error("Failed to load vehicle details");
        onClose();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockNo]);

  const DetailField = ({ label, value }) => (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>{label}</div>
      <div className="text-[14px] font-semibold" style={{ color: "#0f172a" }}>{value || "—"}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="vehicle-detail-drawer">

        {loading || !data ? (
          <div className="p-16 text-center">
            <div className="w-8 h-8 border-2 rounded-full animate-spin mx-auto" style={{ borderColor: "#1e5bb8", borderTopColor: "transparent" }} />
          </div>
        ) : (
          <>
            {/* Header + vehicle details + Facebook actions, all together up top */}
            <div className="p-5 sticky top-0 bg-white z-10" style={{ borderBottom: "1px solid #eef2f8" }}>
              <div className="flex items-start justify-between mb-4">
                <div className="font-heading text-xl font-bold" style={{ color: "#0f172a" }}>
                  {[data.vehicle_year, data.vehicle_make, data.vehicle_model].filter(Boolean).join(" ") || "Unknown vehicle"}
                </div>
                <button onClick={onClose} className="h-8 w-8 flex items-center justify-center rounded-lg shrink-0" style={{ color: "#94a3b8" }}>
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 p-3 rounded-xl mb-4" style={{ background: "#f8fafc", border: "1px solid #eef2f8" }}>
                <DetailField label="Stock No." value={data.stock_no} />
                <DetailField label="Make" value={data.vehicle_make} />
                <DetailField label="Model" value={data.vehicle_model} />
                <DetailField label="Year" value={data.vehicle_year} />
                <DetailField label="Odometer" value={data.odometer ? `${Number(data.odometer).toLocaleString()} km` : null} />
                <DetailField label="Parts Available" value={`${data.parts.length}`} />
              </div>

              <div className="flex items-center gap-2">
                <button onClick={() => listToFacebookPage(stockNo)} disabled={posting}
                  className="flex-1 h-11 rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold"
                  style={{ background: "#eff6ff", color: "#1e40af", border: "1px solid #93c5fd" }}
                  data-testid="vehicle-list-fb-page">
                  <Facebook className="h-4 w-4" /> {posting ? "Posting…" : "List to FB Page"}
                </button>
                <button onClick={() => listToMarketplace(stockNo)}
                  className="flex-1 h-11 rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold"
                  style={{ background: "#f0fdf4", color: "#15803d", border: "1px solid #86efac" }}
                  data-testid="vehicle-list-marketplace">
                  <Store className="h-4 w-4" /> Marketplace
                </button>
              </div>

              {fbInfo && (
                <div className="p-3 rounded-xl mt-2" style={{ background: "#f8fafc", border: "1px solid #eef2f8" }}>
                  <div className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: fbInfo.already_listed ? "#92400e" : "#15803d" }}>
                    {fbInfo.already_listed
                      ? <><Info className="h-3.5 w-3.5" /> Already listed on Facebook</>
                      : <><CheckCircle2 className="h-3.5 w-3.5" /> Posted successfully</>}
                  </div>
                  <div className="text-[11px] mt-1" style={{ color: "#94a3b8" }}>
                    {new Date(fbInfo.posted_at).toLocaleString("en-AU")} · by {fbInfo.posted_by}
                  </div>
                  <a href={fbInfo.post_url} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1 text-[12px] mt-2 font-semibold hover:underline" style={{ color: "#1e5bb8" }}>
                    <ExternalLink className="h-3 w-3" /> View listing
                  </a>
                  {fbInfo.already_listed && (
                    <div className="text-[11px] mt-2" style={{ color: "#64748b" }}>
                      There's no automatic way to remove a Facebook post — to take this listing down, open the link above and delete it manually.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Gallery */}
            {data.images.length > 0 && (
              <div className="p-5" style={{ borderBottom: "1px solid #eef2f8" }}>
                <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#94a3b8" }}>Photos ({data.images.length})</div>
                <div className="grid grid-cols-3 gap-2">
                  {data.images.map((url, i) => (
                    <button key={i} onClick={() => setLightbox(url)}
                      className="aspect-square rounded-xl overflow-hidden" style={{ background: "#f5f7fb" }}>
                      <img src={proxyImg(url)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Parts list */}
            <div className="p-5">
              <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#94a3b8" }}>Parts from this vehicle</div>
              <div className="space-y-1.5">
                {data.parts.map(p => {
                  const clickable = typeof onOpenPart === "function";
                  return (
                    <div key={p.id}
                      onClick={clickable ? () => onOpenPart(p.id) : undefined}
                      className={`flex items-center justify-between gap-2 p-2.5 rounded-xl ${clickable ? "cursor-pointer hover:shadow-sm transition-shadow" : ""}`}
                      style={{ background: "#f8fafc" }}
                      data-testid={`vehicle-part-row-${p.sku}`}>
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium truncate" style={{ color: clickable ? "#1e5bb8" : "#0f172a" }}>{p.name}</div>
                        <div className="text-[11px] font-mono" style={{ color: "#94a3b8" }}>SKU {p.sku}</div>
                      </div>
                      <div className="text-[13px] font-mono font-bold shrink-0" style={{ color: "#0f172a" }}>{formatAud(p.price)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6" style={{ background: "rgba(0,0,0,0.85)" }} onClick={() => setLightbox(null)}>
          <img src={proxyImg(lightbox)} alt="" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}
    </div>
  );
}